import { useMemo } from 'react';
import { useUnrealizedGainLossQuery } from '../api/useUnrealizedGainLossQuery';
import { useRouteParams } from './useRouteParams';
import { useTransactionFilters } from './useTransactionFilters';

export interface Cell {
  text: string | number;
  align?: 'left' | 'right';
  type?: 'currency' | 'number' | 'date' | 'percent';
  fontWeight?: 400 | 600;
  isMobile?: boolean;
}

type LotDetailsRow = {
  id: string;
  cells: Cell[];
};

type ChildRow = {
  id: string;
  cells: Cell[];
  lotDetailsRows: LotDetailsRow[];
};

export type Row = {
  id: string;
  cells: Cell[];
  isTotal?: boolean;
  childRows?: ChildRow[];
};

export interface TableData {
  headers: Cell[];
  rows: Row[];
}

export const useUnrealizedGainLoss = () => {
  const { accountId, groupId, clientId } = useRouteParams();

  const transactionFilters = useTransactionFilters();

  const queryInfo = useUnrealizedGainLossQuery({
    clientId,
    accountId,
    groupId,
    period: transactionFilters.period.value,
    startDate: transactionFilters.period.dates[0],
    endDate: transactionFilters.period.dates[1],
  });

  const data = useMemo(() => {
    if (!queryInfo.data) {
      return null;
    }

    const tableData = {} as TableData;

    tableData.headers = [
      {
        text: 'Name',
        isMobile: true,
      },
      {
        text: 'Account Number',
      },
      {
        text: 'Ticker',
      },

      {
        text: 'Units',
      },
      {
        text: 'Price',
      },
      {
        text: 'Market Value',
      },
      {
        text: 'Allocation',
      },
      {
        text: 'Cost Basis',
      },

      {
        text: 'Short Term Gain Loss',
      },
      {
        text: 'Long Term Gain Loss',
      },
      {
        text: 'Gain Loss',
        isMobile: true,
      },
      {
        text: 'Acquired Date',
      },
      {
        text: 'Holding Period',
      },
    ];

    tableData.rows = queryInfo.data.map(
      (
        {
          name,
          accountNumber,
          costBasis,
          shortTerm,
          longTerm,
          marketValue,
          percent,
          gainLoss,
          holdingPeriod,
          positions,
        },
        index,
      ) => ({
        id: index.toString(),
        cells: [
          { text: name ?? '', align: 'left', isMobile: true },
          { text: accountNumber ?? '', align: 'right' },
          { text: '', align: 'right' },
          {
            text: '',
            type: 'number',
            align: 'right',
          },
          {
            text: '',
            type: 'number',
            align: 'right',
          },
          {
            text: marketValue ?? '',
            type: 'currency',
            align: 'right',
          },
          {
            text: percent ?? '',
            type: 'percent',
            align: 'right',
          },
          {
            text: costBasis ?? '',
            type: 'currency',
            align: 'right',
          },
          {
            text: shortTerm ?? '',
            type: 'currency',
            align: 'right',
          },
          {
            text: longTerm ?? '',
            type: 'currency',
            align: 'right',
          },
          {
            text: gainLoss ?? '',
            type: 'currency',
            align: 'right',
            isMobile: true,
          },
          {
            text: '',
            align: 'right',
          },
          {
            text: holdingPeriod ?? '',
            align: 'left',
          },
        ] as Cell[],
        childRows: [
          ...positions.map(
            (
              {
                name,
                accountNumber,
                ticker,
                units,
                price,
                costBasis,
                shortTerm,
                longTerm,
                gainLoss,
                marketValue,
                percent,
                taxLotDetails,
              },
              index,
            ) => ({
              id: index.toString(),
              cells: [
                { text: name ?? '', align: 'left' },
                { text: accountNumber ?? '', align: 'right' },
                { text: ticker ?? '', align: 'right' },
                { text: units ?? '', align: 'right', type: 'number' },
                { text: price ?? '', align: 'right', type: 'currency' },
                { text: marketValue ?? '', align: 'right', type: 'currency' },
                { text: percent ?? '', align: 'right', type: 'percent' },
                { text: costBasis ?? '', align: 'right', type: 'currency' },
                { text: shortTerm ?? '', align: 'right', type: 'currency' },
                { text: longTerm ?? '', align: 'right', type: 'currency' },
                { text: gainLoss ?? '', align: 'right', type: 'currency' },
                { text: '', align: 'left' },
                { text: '', align: 'left' },
              ] as Cell[],
              lotDetailsRows: [
                ...taxLotDetails.map(
                  (
                    {
                      acquiredDate,
                      costBasis,
                      holdingPeriod,
                      longTerm,
                      marketValue,
                      price,
                      percent,
                      shortTerm,
                      total,
                      units,
                    },
                    index,
                  ) => ({
                    id: index.toString(),
                    cells: [
                      { text: '', align: 'right' },
                      { text: '', align: 'right' },
                      { text: '', align: 'right' },
                      { text: units ?? '', align: 'right', type: 'number' },
                      { text: price ?? '', align: 'right', type: 'currency' },
                      { text: marketValue ?? '', align: 'right', type: 'currency' },
                      { text: percent ?? '', align: 'right', type: 'percent' },
                      { text: costBasis ?? '', align: 'right', type: 'currency' },
                      { text: shortTerm ?? '', align: 'right', type: 'currency' },
                      { text: longTerm ?? '', align: 'right', type: 'currency' },
                      { text: total ?? '', align: 'right', type: 'currency' },
                      { text: acquiredDate ?? '', align: 'left', type: 'date' },
                      { text: holdingPeriod ?? '', align: 'left' },
                    ] as Cell[],
                  }),
                ),
              ],
            }),
          ),
        ],
      }),
    );

    const totals = queryInfo.data.reduce(
      (acc, item) => {
        acc.shortTerm += item.shortTerm;
        acc.longTerm += item.longTerm;
        acc.total += item.gainLoss;
        return acc;
      },
      { shortTerm: 0, longTerm: 0, total: 0 },
    );

    return { tableData, totals, gainLoss: queryInfo.data };
  }, [queryInfo]);

  return { ...queryInfo, data };
};
